import * as React from "react";
import { useStores } from "#stores/index";

interface UseTabUrlOptions<T extends string> {
	// NoInfer impede que T seja narrowing para o literal de initialKey quando
	// o caller não passa T explícito — sem isso, useTabUrl({ initialKey: "default" })
	// inferiria T = "default" e onChangeTab rejeitaria qualquer outra string.
	initialKey: NoInfer<T>;
	validateTab?: (tab: string) => T;
}

/**
 * Sincroniza a aba ativa com `routerStore.location.hash`.
 *
 * Contrato de reatividade: o componente que usa este hook precisa re-renderizar
 * quando `routerStore.location` muda. Como `routerStore` é um observable MobX,
 * isso normalmente exige envolver o componente em `observer(...)` (ou outro
 * mecanismo reativo equivalente). Sem isso, navegações via `routerStore.push`
 * mudam o hash mas o hook não dispara o efeito de sincronização.
 */
export function useTabUrl<T extends string = string>({
	initialKey,
	validateTab,
}: UseTabUrlOptions<T>) {
	const { routerStore } = useStores();

	const resolve = (raw: string): T => {
		const hash = raw.replace(/^#/, "");
		const candidate = (hash || initialKey) as T;
		return validateTab ? validateTab(candidate) : candidate;
	};

	const [currentTab, setCurrentTab] = React.useState<T>(() =>
		resolve(routerStore.location?.hash ?? ""),
	);

	const onChangeTab = (tab: T) => {
		const next = validateTab ? validateTab(tab) : tab;
		routerStore.push(`${window.location.pathname}#${next}`);
		setCurrentTab(next);
	};

	// Limitação conhecida: validateTab fica fora das deps. Se sua identidade mudar
	// mid-render (ex.: feature flag resolvendo após o mount), o efeito não re-roda
	// e um hash já validado não é reavaliado. Consumidores devem garantir que
	// validateTab esteja estável no momento em que o hash for lido.
	React.useEffect(() => {
		const rawHash = (routerStore.location?.hash ?? "").replace(/^#/, "");
		const next = resolve(routerStore.location?.hash ?? "");
		setCurrentTab(next);
		// Se validateTab reescreveu o hash, corrige a URL via replace para não dessincronizar
		// estado e URL ao recarregar ou compartilhar o link.
		if (rawHash && rawHash !== next) {
			routerStore.replace(`${window.location.pathname}#${next}`);
		}
	}, [routerStore.location?.hash]);

	return {
		defaultActiveKey: currentTab,
		onChangeTab,
		currentTab,
	};
}
